import { Injectable } from "@angular/core";
import { BaseService } from "../../@core/services/base.service";
import { FORM_QUESTION_STATUS, IContract, IFormAnswer, IFormQuestion } from "../models/contract-management.model";
import { environment } from "../../../environments/environment.dev";
import { HttpClient } from "@angular/common/http";
import { USER_TYPE } from "../../@core/models/enums";
import { IFormFilter } from "../components/document-form/document-form.component";


export interface IFormMetrics {
    totalExtractedQuestions: number,
    totalAnsweredQuestions: number,
    totalPendingQuestions: number,
    totalApprovedQuestions: number,
    totalRejectedQuestions: number,
    totalFlaggedQuestions: number,
}
@Injectable({ providedIn: 'root' })
export class DocumentFormService extends BaseService {

    apiUrl = environment.apiUrl + '/form'

    constructor(private httpClient: HttpClient) {
        super(httpClient);
    }

    changeQuestionStatus(data: { contract: IContract['_id'], documentForm: string, questionId: string, status: FORM_QUESTION_STATUS }) {
        return this.post<{
            metrics: IFormMetrics
        }>(this.apiUrl + '/change-answer-status', data)
    }

    formMetrics(documentForm: string, filter: IFormFilter = {}) {
        return this.post<{
            metrics: IFormMetrics
        }>(this.apiUrl + '/metrics/' + documentForm, { filter })
    }

    downloadForm(documentFormId: string) {

        return this.get<{ data: Uint8Array }>(this.apiUrl + '/download-questionnaire/' + documentFormId);
    }

    updateQuestionTitle(data: { contract: IContract['_id'], documentForm: string, questionId: string, title: string }) {
        return this.put<{
            question: string
        }>(this.apiUrl + '/update-question-title', data)
    }

    assignUsersToQuestion(data: { contract: IContract['_id'], documentForm: string, questionId: string, users: { user: string, status: FORM_QUESTION_STATUS, email: string, name: string, type: USER_TYPE }[] }) {
        return this.post<{
            question: IFormQuestion
        }>(this.apiUrl + '/assign-users-to-question', data)
    }

    changeAnswerValue(data: { contract: IContract['_id'], documentForm: string, answerId: string, value: string, type: string, cells: string[][] }) {
        return this.put<{
            question: IFormQuestion
        }>(this.apiUrl + '/change-answer-value', data)
    }

    updateAttachmentTypeQuestion(data: FormData) {
        return this.put<IFormAnswer>(this.apiUrl + '/change-answer-value-attachment', data)
    }

    addQuestion(documentForm: string, section: string, questionData: Partial<IFormQuestion>) {
        return this.post(this.apiUrl + '/add-question', { documentForm, section, questionData })
    }

    /** Re-trigger question extraction for a document form (used after credits are topped up). */
    extractQuestions(documentFormId: string) {
        return this.get(this.apiUrl + `/extract-questions`, { documentForm: documentFormId } as any);
    }

    retryAnswer(documentFormId: string, retryOptions: {
        answerId: string, prompt: string, qnaBank: boolean, repository: boolean
    }) {
        return this.post(this.apiUrl + `/retry-answer`, { documentFormId, ...retryOptions })
    }

    bulkRetryAnswers(documentFormId: string, sectionId: string, questionIds: string[], retryOptions: {
        prompt: string, qnaBank: boolean, repository: boolean
    }) {
        return this.post(this.apiUrl + '/bulk-retry-answers', { documentFormId, sectionId, questionIds, ...retryOptions })
    }

    updateWatchers(data: { contractId: IContract['_id'], questionId: string, add: boolean }) {
        return this.post(this.apiUrl + '/update-watchers', data)
    }

    bulkAssignUsersToQuestions(data: { contractId: string, questions: string[], data: { user: string, status: FORM_QUESTION_STATUS, email: string, name: string, type: USER_TYPE }[] }) {
        return this.post(this.apiUrl + '/bulk-assign-users', data)
    }

    deleteSection(documentFormId: string, sectionId: string) {
        return this.delete(this.apiUrl + `/delete-section`, { documentFormId, sectionId });
    }

    deleteQnA(documentFormId: string, questionIds: string[], answerIds: string[]) {
        return this.delete(this.apiUrl + `/delete-qna-from-section`, { documentFormId, questionIds, answerIds })
    }
}
